import { getAttributeQty, getAttributeValue } from "../../../../../utils/formulaScope";
import { findManufacturerProductId, findSubcategory } from "./productHelpers";

export const MULTI_LINE_MATERIAL_CATEGORIES = ["Furnace Jacks", "Furnace Cap", "Furnace Collar"] as const;

/** Categories omitted from Estimate materials UI (still valid in DB/catalog). */
export const HIDDEN_MATERIAL_CATEGORIES = ["Misc. Materials"] as const;

export function isVisibleMaterialCategory(name: string | undefined): boolean {
    if (!name) return false;
    return !(HIDDEN_MATERIAL_CATEGORIES as readonly string[]).includes(name);
}

export type MultiLineMaterialCategory = (typeof MULTI_LINE_MATERIAL_CATEGORIES)[number];

export interface MaterialLine {
    lineKey: string;
    value: string;
    quantity: number;
    manufacturerProductId?: string;
    id?: string;
    systemLine?: string;
    color?: string;
    supplierId?: string;
    supplierName?: string;
    unitPrice?: number;
    awaitingColor?: boolean;
}

export interface MultiLineMaterialSelection {
    multiLine: true;
    lines: MaterialLine[];
}

export interface FurnaceVentEntry {
    size: string;
    count: number;
}

export function isMultiLineCategory(category: string): category is MultiLineMaterialCategory {
    return (MULTI_LINE_MATERIAL_CATEGORIES as readonly string[]).includes(category);
}

export function isMultiLineSelection(selection: any): selection is MultiLineMaterialSelection {
    return selection?.multiLine === true && Array.isArray(selection?.lines);
}

export function parseFurnaceVentEntries(attributes: any[] | undefined): FurnaceVentEntry[] {
    const legacyQty = getAttributeQty(attributes, "Furnace Vents");
    if (legacyQty > 0) {
        return [{ size: "4 inch", count: legacyQty }];
    }

    const raw = getAttributeValue(attributes, "Furnace Vent Size");
    if (!raw) return [];

    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return parsed
                .map((entry: any) => ({
                    size: String(entry?.size || "").trim(),
                    count: Math.max(0, Number(entry?.count) || 0),
                }))
                .filter((entry) => entry.size && entry.count > 0);
        }
    } catch {
        // not JSON
    }

    if (raw.trim()) {
        return [{ size: raw.trim(), count: 1 }];
    }
    return [];
}

export function buildMaterialLine(
    materialSchema: any[],
    category: string,
    productValue: string,
    quantity: number,
    existingLine?: MaterialLine,
): MaterialLine | null {
    if (!productValue || quantity <= 0) return null;

    const sub = findSubcategory(materialSchema, category, productValue);
    const manufacturerProductId =
        existingLine?.value === productValue && existingLine.manufacturerProductId
            ? existingLine.manufacturerProductId
            : findManufacturerProductId(materialSchema, category, productValue);

    return {
        lineKey: productValue,
        value: productValue,
        quantity,
        manufacturerProductId,
        systemLine: sub?.SystemLine || productValue,
        id: existingLine?.value === productValue ? existingLine.id : undefined,
        supplierId: existingLine?.value === productValue ? existingLine.supplierId : undefined,
        supplierName: existingLine?.value === productValue ? existingLine.supplierName : undefined,
        unitPrice: existingLine?.value === productValue ? existingLine.unitPrice : undefined,
    };
}

export function buildLinesFromFurnaceInspection(
    materialSchema: any[],
    category: string,
    attributes: any[] | undefined,
    existingLines: MaterialLine[] = [],
): MaterialLine[] {
    const entries = parseFurnaceVentEntries(attributes);
    const existingByKey = new Map(existingLines.map((line) => [line.lineKey, line]));

    return entries
        .map((entry) =>
            buildMaterialLine(
                materialSchema,
                category,
                entry.size,
                entry.count,
                existingByKey.get(entry.size),
            ),
        )
        .filter((line): line is MaterialLine => line != null);
}

export function normalizeMaterialQuantity(quantity: unknown): number {
    const num = Number(quantity);
    return Number.isFinite(num) ? num : 0;
}

export function materialQuantitiesDiffer(a: unknown, b: unknown, epsilon = 0.001): boolean {
    return Math.abs(normalizeMaterialQuantity(a) - normalizeMaterialQuantity(b)) > epsilon;
}

export function materialSelectionsSignature(values: Record<string, any>): string {
    const flat = flattenMaterialSelectionsForSync(values);
    const keys = Object.keys(flat).sort();
    return JSON.stringify(flat, keys);
}

export function isLaborCartItem(item: { ProductCategory?: string; LaborServiceId?: string | null }): boolean {
    return item.ProductCategory === "Labor" || !!item.LaborServiceId;
}

/** Flatten nested UI selections into sync keys for cart diffing. */
export function flattenMaterialSelectionsForSync(
    uiState: Record<string, any>,
    preserveIdsFrom?: Record<string, any>,
): Record<string, any> {
    const flat: Record<string, any> = {};

    Object.entries(uiState).forEach(([category, selection]) => {
        if (!selection) return;

        if (isMultiLineSelection(selection)) {
            selection.lines.forEach((line) => {
                const key = `${category}::${line.lineKey}`;
                flat[key] = {
                    ...line,
                    category,
                    lineKey: line.lineKey,
                    quantity: normalizeMaterialQuantity(line.quantity),
                    id: preserveIdsFrom?.[key]?.id || line.id,
                };
            });
            return;
        }

        flat[category] = {
            ...selection,
            category,
            quantity: normalizeMaterialQuantity(selection.quantity),
            id: preserveIdsFrom?.[category]?.id || selection.id,
        };
    });

    return flat;
}

export function hydrateMultiLineFromCart(
    hydratedSelections: Record<string, any>,
    cartItem: any,
): void {
    const category = cartItem.ProductCategory;
    if (!category || !isMultiLineCategory(category)) return;

    const lineKey = cartItem.ProductName || cartItem.ManufacturerProductName || "";
    if (!lineKey) return;

    if (!hydratedSelections[category] || !isMultiLineSelection(hydratedSelections[category])) {
        hydratedSelections[category] = { multiLine: true, lines: [] };
    }

    const selection = hydratedSelections[category] as MultiLineMaterialSelection;
    const existingIdx = selection.lines.findIndex((line) => line.lineKey === lineKey);
    const line: MaterialLine = {
        lineKey,
        value: lineKey,
        quantity: cartItem.ItemCount,
        id: cartItem.Id,
        color: cartItem.Color,
        manufacturerProductId: cartItem.ManufacturerProductId,
        systemLine: cartItem.SystemLine,
        supplierId: cartItem.SupplierId,
        supplierName: cartItem.SupplierName,
        unitPrice: cartItem.UnitPrice,
    };

    if (existingIdx >= 0) {
        selection.lines[existingIdx] = line;
    } else {
        selection.lines.push(line);
    }
}

export function getDefaultLineProduct(productOptions: string[], usedProducts: Set<string>): string | null {
    const unused = productOptions.find((opt) => !usedProducts.has(opt));
    return unused || productOptions[0] || null;
}

export function getMultiLineTotalQuantity(lines: MaterialLine[]): number {
    return lines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
}

/** Muted accent for the left edge of each size row. */
export function getSizeAccentColor(size: string): string {
    const key = size.trim().toLowerCase();
    if (key.startsWith("4")) return "#818cf8";
    if (key.startsWith("5")) return "#a78bfa";
    if (key.startsWith("6")) return "#f472b6";
    if (key.startsWith("7")) return "#fb923c";
    return "#94a3b8";
}
